import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Campaign, DMAutomation, MassText } from "@markit/common.types";
import { getDoc, onSnapshot } from "../../firebase";
import {
  getCampaignMassTextsSnap,
  getUserCampaignRef,
  getUserDmAutomationData,
} from "../../utils/FirebaseUtils";
import { useDispatch } from "react-redux";
import {
  campaignActions,
  CampaignDetails,
  MassTextDetails,
} from "../../redux/slices/campaignSlice";
import useOnUnmount from "../useOnUnmount";
import { getMassTextAttachments } from "../../utils/textingUtils";
import { uniqueVals } from "@markit/common.utils";
import {
  fetchCampaignDetails,
  fetchEventCampaignData,
} from "../../utils/campaignUtils";

type useLiveUpdatingCampaignProps = {
  initialCampaignId: string;
  userId: string;
  eventId?: string; // If viewing from event dashboard
};

export const useLiveUpdatingCampaign = (
  props: useLiveUpdatingCampaignProps
) => {
  const { initialCampaignId, userId, eventId } = props;
  const dispatch = useDispatch();
  const [fetchedCampaign, setFetchedCampaign] = useState<Campaign>();
  const [fetchedCampaignDetail, setFetchedCampaignDetail] =
    useState<CampaignDetails>();
  const [fetchedDmAutomation, setFetchedDmAutomation] =
    useState<DMAutomation>();
  const [isError, setIsError] = useState(false);
  const unsubscribeArrayRef = useRef<(() => void)[]>([]);

  const loaded = useMemo(
    () => fetchedCampaign !== undefined && fetchedCampaignDetail !== undefined,
    [fetchedCampaign, fetchedCampaignDetail]
  );

  useOnUnmount(
    (latestDeps) => {
      if (latestDeps[0]) {
        dispatch(campaignActions.modifyCampaign(latestDeps[0] as Campaign));
        dispatch(
          campaignActions.modifyCampaignDetails(
            latestDeps[1] as CampaignDetails
          )
        );
      }
      unsubscribeArrayRef.current.forEach(
        (unsubscribe) => unsubscribe && unsubscribe()
      );
      unsubscribeArrayRef.current = []; // Clear the array after cleanup
    },
    [fetchedCampaign, fetchedCampaignDetail]
  );

  const addCampaignDetail = useCallback((campaignDetail: CampaignDetails) => {
    setFetchedCampaignDetail(campaignDetail);
  }, []);

  const addMassText = useCallback(
    async (massTextDetails: MassTextDetails[]) => {
      setFetchedCampaignDetail((fetchedCampaignDetail) => {
        if (!fetchedCampaignDetail) {
          return undefined;
        }
        const updatedMassTextDetails = uniqueVals(
          [...massTextDetails, ...fetchedCampaignDetail.massTextDetails],
          (text: MassTextDetails) => text.massText.id
        );
        return {
          ...fetchedCampaignDetail,
          massTextDetails: updatedMassTextDetails,
        };
      });
    },
    []
  );

  const removeMassText = useCallback((massTextId: string) => {
    setFetchedCampaignDetail((fetchedCampaignDetail) => {
      if (!fetchedCampaignDetail) {
        return undefined;
      }
      const newMassTextDetails = fetchedCampaignDetail?.massTextDetails.filter(
        (text) => text.massText.id !== massTextId
      );
      return {
        ...fetchedCampaignDetail,
        massTextDetails: newMassTextDetails,
      };
    });
  }, []);

  const fetchMassTextDetails = useCallback(
    async (massText: MassText) => {
      const attachments = await getMassTextAttachments(userId, massText);
      const newMassTextDetail: MassTextDetails = {
        massText: massText,
        attachments: attachments,
      };
      return newMassTextDetail;
    },
    [userId]
  );

  useEffect(() => {
    // Reset state so `loaded` correctly reflects the new campaignId's load status
    setFetchedCampaign(undefined);
    setFetchedCampaignDetail(undefined);
    setIsError(false);

    let isMounted = true;
    const localUnsubscribes: (() => void)[] = [];

    const setup = async () => {
      let campaignId = initialCampaignId;
      if (!initialCampaignId && eventId) {
        const campaign = await fetchEventCampaignData(userId, eventId);
        if (!isMounted) return;
        if (campaign) {
          campaignId = campaign.id;
        }
      }
      if (!campaignId) return;

      let campaignSnap;
      try {
        campaignSnap = await getDoc(getUserCampaignRef(userId, campaignId));
      } catch (error) {
        console.error("Error loading user campaign", error);
        return;
      }
      if (!isMounted) return;
      if (!campaignSnap.exists()) {
        setIsError(true);
        return;
      }
      setIsError(false);

      const unsubscribeCampaign = onSnapshot(
        getUserCampaignRef(userId, campaignId),
        async (doc) => {
          if (!isMounted) return;
          const campaignData = doc.data();
          if (campaignData) {
            setFetchedCampaign(campaignData);
            if (campaignData.dmAutomationId) {
              const dmAutomationData = await getUserDmAutomationData(
                userId,
                campaignData.dmAutomationId
              );
              if (dmAutomationData) {
                setFetchedDmAutomation(dmAutomationData);
              }
            }
            const campaignDetails = await fetchCampaignDetails(
              userId,
              campaignData
            );
            if (isMounted) {
              addCampaignDetail(campaignDetails);
            }
          }
        }
      );
      localUnsubscribes.push(unsubscribeCampaign);

      const massTextsSnap = await getCampaignMassTextsSnap(userId, campaignId);
      if (!isMounted) return;
      const unsubscribeTexts = onSnapshot(massTextsSnap.query, (snapshot) => {
        snapshot.docChanges().forEach(async (message) => {
          if (!isMounted) return;
          const massText = message.doc.data();
          // TODO: massTextDetails gets fetched twice here and in fetchCampaignDetails
          const massTextDetail = await fetchMassTextDetails(massText);
          if (!isMounted) return;
          if (message.type === "added" || message.type === "modified") {
            addMassText([massTextDetail]);
          } else if (message.type === "removed") {
            removeMassText(massText.id);
          }
        });
      });
      localUnsubscribes.push(unsubscribeTexts);

      unsubscribeArrayRef.current = localUnsubscribes;
    };

    setup();

    return () => {
      isMounted = false;
      localUnsubscribes.forEach((unsubscribe) => unsubscribe && unsubscribe());
      unsubscribeArrayRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCampaignId, userId, eventId]);

  return {
    liveCampaign: fetchedCampaign,
    liveCampaignDetail: fetchedCampaignDetail,
    isLiveCampaignError: isError,
    loaded: loaded,
    dmAutomation: fetchedDmAutomation,
  };
};
